import { useQueries } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { invokeWithRetry } from '@/lib/supabaseRetry';
import * as Sentry from '@sentry/react';
import type { ExperimentResults } from './useExperimentResults';

export const useBulkExperimentResults = (experimentIds: string[]) => {
  const queries = useQueries({
    queries: (experimentIds || []).map((experimentId) => ({
      queryKey: ['experiment-results', experimentId],
      enabled: !!experimentId,
      queryFn: async (): Promise<ExperimentResults> => {
        try {
          const { data: apiResponse, error } = await invokeWithRetry('tastermonial-api', {
            body: {
              method: 'GET',
              endpoint: `/api/v2/experiments/${experimentId}/results`,
            },
          });
          if (error) {
            Sentry.captureException(error);
            throw new Error(error.message || 'Failed to fetch experiment results');
          }
          if (apiResponse.status !== 200) {
            throw new Error(`API error: ${apiResponse.statusText || 'Failed to fetch experiment results'}`);
          }
          const resultsData = apiResponse.data as ExperimentResults;
          
          // Calculate local timestamps for each meal impact
          const processedResults: ExperimentResults = {
            ...resultsData,
            meal_impacts: resultsData.meal_impacts.map(impact => {
              const utcDate = new Date(impact.meal_timestamp_utc);
              const localDate = new Date(utcDate.getTime() + (impact.timezone * 60 * 60 * 1000));
              return {
                ...impact,
                experiment_timestamp_local: localDate.toISOString()
              };
            })
          };
          
          return processedResults;
        } catch (err) {
          Sentry.captureException(err);
          throw err;
        }
      },
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);
  const resultsByExperimentId: Record<string, ExperimentResults | undefined> = {};
  queries.forEach((q, idx) => {
    const id = experimentIds[idx];
    resultsByExperimentId[id] = q.data as ExperimentResults | undefined;
  });

  return { resultsByExperimentId, isLoading, isError };
};
